import type { TFunction } from "i18next";
import { getActiveChatSessionId, storeChat } from "src/services/ChatbotAPI";
import { getApiLangFromI18n } from "src/utils/apiLang";
import i18n from "src/i18n/config";
import {
  CUSTOM_OPTION,
  EMAIL_WIZARD_STEPS,
} from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/AiCampaignConstants";
import { EMAIL_TEMPLATE_WIZARD_STEPS } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/Template/TemplateEditorConstants";
import { WHATSAPP_CAMPAIGN_STEPS } from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/WhatsAppCampaignConstants";
import type { ChatCampaignFlowSnapshot } from "./UseChatCampaignFlow";

interface StepSummary {
  title: string;
  values: string;
  stepNumber: number;
  totalSteps: number;
}

const joinValues = (values: (string | null | undefined)[]) => values.filter(Boolean).join(" · ");

const summarizeSnapshot = (snapshot: ChatCampaignFlowSnapshot, t: TFunction): StepSummary | null => {
  switch (snapshot.phase) {
    case "setup": {
      const { result } = snapshot;
      return {
        title: t("campaignFlow.createCampaignNew.title"),
        values: result
          ? joinValues([result.channel, result.campaignName, result.sendTo.join(", ")])
          : "",
        stepNumber: 0,
        totalSteps: 0,
      };
    }
    case "email": {
      const { form, stepIndex, stepKey } = snapshot.snapshot;
      const byStep: Record<string, string> = {
        productCategories: form.selectedCategories.join(", "),
        campaignCategory:
          form.campaignType === CUSTOM_OPTION ? form.customCampaignType : (form.campaignType ?? ""),
        campaignSubCategory:
          form.subCampaign === CUSTOM_OPTION ? form.customSubCampaign : (form.subCampaign ?? ""),
        aiPrompt: joinValues([form.prompt, form.tone, form.persona, form.seasonalTag, form.offer]),
        senderRecipients: form.subjectLine,
        reviewDetails: joinValues([form.campaignName, form.subjectLine]),
      };
      return {
        title: t(EMAIL_WIZARD_STEPS[stepIndex].titleKey),
        values: byStep[stepKey] ?? "",
        stepNumber: stepIndex + 1,
        totalSteps: EMAIL_WIZARD_STEPS.length,
      };
    }
    case "template": {
      const { details, stepIndex, stepKey } = snapshot.snapshot;
      return {
        title: t(EMAIL_TEMPLATE_WIZARD_STEPS[stepIndex].titleKey),
        values: stepKey === "senderRecipients" ? joinValues([details.campaignName, details.subjectLine]) : "",
        stepNumber: stepIndex + 1,
        totalSteps: EMAIL_TEMPLATE_WIZARD_STEPS.length,
      };
    }
    case "whatsapp": {
      const { template, stepIndex } = snapshot.snapshot;
      return {
        title: t(WHATSAPP_CAMPAIGN_STEPS[stepIndex].titleKey),
        values: template?.name ?? "",
        stepNumber: stepIndex + 1,
        totalSteps: WHATSAPP_CAMPAIGN_STEPS.length,
      };
    }
    default:
      return null;
  }
};

const FLOW_PHASES: readonly ChatCampaignFlowSnapshot["phase"][] = [
  "setup",
  "email",
  "template",
  "whatsapp",
];

/** Drops the generated template and the uploaded file, which are large and not JSON-safe. */
const toStorableSnapshot = (snapshot: ChatCampaignFlowSnapshot): ChatCampaignFlowSnapshot => {
  switch (snapshot.phase) {
    case "template":
      return { ...snapshot, snapshot: { ...snapshot.snapshot, template: null } };
    case "whatsapp":
      return {
        ...snapshot,
        snapshot: {
          ...snapshot.snapshot,
          media: { attachment: null, isUploading: false, error: null },
        },
      };
    default:
      return snapshot;
  }
};

/** Reads the `campaign_flow` the backend returns with a stored chat message, if it has one. */
export const parseCampaignFlow = (raw: unknown): ChatCampaignFlowSnapshot | null => {
  try {
    const parsed: unknown = typeof raw === "string" ? JSON.parse(raw) : raw;
    const phase = (parsed as { phase?: unknown } | null)?.phase;
    if (!FLOW_PHASES.some(candidate => candidate === phase)) {
      return null;
    }
    const flow = parsed as ChatCampaignFlowSnapshot;
    const isLegacyWhatsApp =
      flow.phase === "whatsapp" &&
      flow.snapshot.template !== null &&
      !Array.isArray(flow.snapshot.template.variableKeys);
    return isLegacyWhatsApp ? null : flow;
  } catch {
    return null;
  }
};

/**
 * Each finished campaign wizard step is saved as a short text exchange plus its JSON snapshot,
 * so history can rebuild the step (and still reads as plain text if the backend drops the JSON).
 *  message/response text per chat, so each finished campaign wizard step
 * is saved as a short text exchange; history then replays the campaign as readable messages.
 */
export const persistCampaignStep = (snapshot: ChatCampaignFlowSnapshot, t: TFunction) => {
  const sessionId = Number(getActiveChatSessionId());
  const userId = Number(localStorage.getItem("user_id"));
  const summary = summarizeSnapshot(snapshot, t);
  if (!sessionId || !userId || !summary) {
    return;
  }

  const message = summary.values ? `${summary.title}: ${summary.values}` : summary.title;
  const response = summary.totalSteps
    ? t("campaignFlow.history.stepSaved", {
        step: summary.stepNumber,
        total: summary.totalSteps,
        defaultValue: "Step {{step}} of {{total}} completed.",
      })
    : t("campaignFlow.history.setupSaved", { defaultValue: "Campaign details saved." });

  storeChat({
    user_id: userId,
    session_id: sessionId,
    message,
    response,
    session_headline: "AI Chat Session",
    lang: getApiLangFromI18n(i18n),
    campaign_flow: JSON.stringify(toStorableSnapshot(snapshot)),
  }).catch(() => undefined);
};
